import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IContractSignature } from 'src/app/domain/registration/models/IContractSignature';
import { ContractSignatureType } from 'src/app/domain/enums/contract-signature-type.enum';
import {
    IContractablePlan,
    IContractableEntityService,
    IRelatedPersons,
    ContractEntityType
} from 'src/app/domain/registration/interfaces/contract-generation.interfaces';
import { formatDocument, extractDocumentFromEntity } from '../../utils/document-formatter';

/**
 * Serviço abstrato para geração de contratos
 * Fornece métodos reutilizáveis para preparar dados de contrato
 */
@Injectable()
export abstract class AbstractContractGenerationService<T = any> {

    constructor() { }

    /**
     * Extrai informações básicas da entidade para exibição
     */
    extractEntityInfo(entity: any): { name: string; document: string; email: string; phone: string } {
        const document = extractDocumentFromEntity(entity);
        const formattedDocument = formatDocument(document);

        return {
            name: entity.name || entity.companyName || entity.tradingName || 'Sem Nome',
            document: formattedDocument,
            email: entity.email || '',
            phone: entity.cellPhone || entity.phone || ''
        };
    }

    /**
     * Cria assinatura a partir de uma entidade
     */
    createSignatureFromEntity(
        entity: any,
        type: ContractSignatureType,
        code: string,
        flags: { isContracted?: boolean; isDefaultContracted?: boolean } = {}
    ): IContractSignature {
        const document = extractDocumentFromEntity(entity);
        const formattedDocument = formatDocument(document);
        const phone = entity.cellPhone || entity.phone || '';

        return {
            code,
            name: entity.name || entity.companyName || entity.tradingName || 'Sem Nome',
            email: entity.email || '',
            document: formattedDocument,
            whatsAppNumber: phone,
            type,
            status: 'PENDENTE',
            isContracted: flags.isContracted || false,
            isDefaultContracted: flags.isDefaultContracted || false,
            customerId: entity.id
        };
    }

    /**
     * Cria múltiplas assinaturas a partir de pessoas relacionadas
     */
    createRelatedPersonsSignatures(
        relatedPersons: IRelatedPersons,
        existingEmails: Set<string>,
        codeGenerator: () => string
    ): IContractSignature[] {
        const signatures: IContractSignature[] = [];

        const personTypeMap: Array<{
            key: keyof IRelatedPersons;
            type: ContractSignatureType;
        }> = [
                { key: 'partner', type: ContractSignatureType.PARCEIRO },
                { key: 'consultant', type: ContractSignatureType.CONSULTOR },
                { key: 'representative', type: ContractSignatureType.REPRESENTANTE },
                { key: 'counter', type: ContractSignatureType.CONTADOR }
            ];

        personTypeMap.forEach(({ key, type }) => {
            const person = relatedPersons[key];
            if (person && person.email && !existingEmails.has(person.email.toLowerCase())) {
                signatures.push(this.createSignatureFromEntity(person, type, codeGenerator()));
                existingEmails.add(person.email.toLowerCase());
            }
        });

        return signatures;
    }

    /**
     * Filtra planos ativos e válidos
     */
    filterActivePlans(plans: IContractablePlan[]): IContractablePlan[] {
        return plans.filter(plan => plan.active && plan.canceledAt === null);
    }

    /**
     * Verifica se um plano tem template de contrato
     */
    hasContractTemplate(plan: IContractablePlan): boolean {
        return !!(plan.system?.contractTemplateId);
    }

    /**
     * Verifica se o contrato está assinado
     */
    isContractSigned(status: string | undefined): boolean {
        if (!status) return false;
        return status.toUpperCase() === 'SIGNED';
    }

    /**
     * Prepara dados para geração de contrato
     */
    prepareContractData(
        entityId: string,
        entityType: ContractEntityType,
        planId: string,
        systemId: string,
        signatures: IContractSignature[],
        relatedPersonIds?: {
            partnerId?: string;
            consultantId?: string;
            representativeId?: string;
            counterId?: string;
            unitId?: string;
        },
        customHtmlContent?: string
    ): any {
        const baseData = {
            entityId,
            entityType,
            planId,
            systemId,
            ...relatedPersonIds,
            additionalSigners: signatures.map(sig => ({
                name: sig.name,
                email: sig.email,
                document: sig.document,
                whatsAppNumber: sig.whatsAppNumber || '',
                role: this.getSignatureTypeLabel(sig.type),
                isDefaultContracted: sig.isDefaultContracted,
                isContracted: sig.isContracted
            }))
        };

        // Adicionar customerId para compatibilidade com backend existente
        if (entityType === 'customer') {
            return {
                ...baseData,
                customerId: entityId,
                htmlContent: customHtmlContent
            };
        }

        return {
            ...baseData,
            htmlContent: customHtmlContent
        };
    }

    /**
     * Obtém label de um tipo de assinatura
     */
    getSignatureTypeLabel(type: ContractSignatureType): string {
        if (typeof type === 'string') {
            return type;
        }

        const typeName = Object.keys(ContractSignatureType).find(key =>
            ContractSignatureType[key as keyof typeof ContractSignatureType] === type
        );

        return typeName || 'Desconhecido';
    }

    /**
     * Gera código sequencial para assinatura
     */
    generateSignatureCode(currentCount: number): string {
        return (currentCount + 1).toString().padStart(3, '0');
    }

    /**
     * Método abstrato para obter o nome da entidade (deve ser implementado por classes filhas)
     */
    protected abstract getEntityName(entity: any): string;
}


